import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Mail, Send, Users, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEGMENTS = [
  { id: "all", label: "전체 활성 회원" },
  { id: "paid", label: "유료/정회원" },
  { id: "free", label: "무료 회원" },
  { id: "male", label: "남성" },
  { id: "female", label: "여성" },
];

export default function AdminBulkEmailPage() {
  const { token } = useAuth();
  const [segment, setSegment] = useState("all");
  const [count, setCount] = useState(null);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [campaign, setCampaign] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  const authHdr = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchCount = async (seg) => {
    try {
      const r = await fetch(`${API_URL}/api/admin/bulk-email/audience?segment=${seg}`, { headers: authHdr });
      const d = await r.json();
      setCount(d.recipient_count);
    } catch { setCount(null); }
  };

  const fetchCampaigns = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/bulk-email/campaigns`, { headers: authHdr });
      const d = await r.json();
      setCampaigns(d.campaigns || []);
    } catch {}
  };

  useEffect(() => { if (token) { fetchCount(segment); } }, [segment, token]);
  useEffect(() => { if (token) fetchCampaigns(); }, [token]);

  const createCampaign = async () => {
    if (!subject.trim() || !html.trim()) { toast.error("제목과 본문을 입력하세요."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/bulk-email/campaigns`, {
        method: "POST", headers: authHdr,
        body: JSON.stringify({ subject, html, segment }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "생성 실패");
      setCampaign(d);
      toast.success(`캠페인 생성 완료 · 대상 ${d.recipient_count}명`);
      fetchCampaigns();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!campaign) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/bulk-email/campaigns/${campaign.id}/test`, {
        method: "POST", headers: authHdr, body: JSON.stringify({}),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.message || d.detail || "테스트 실패");
      toast.success(`테스트 메일 발송: ${d.test_email}`);
      setCampaign({ ...campaign, test_sent: true });
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const sendReal = async () => {
    if (!campaign) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/bulk-email/campaigns/${campaign.id}/send`, {
        method: "POST", headers: authHdr,
        body: JSON.stringify({ confirm_token: "SEND_BULK_EMAIL_CONFIRM" }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "발송 실패");
      toast.success(d.message || "발송 시작");
      setConfirming(false); setCampaign(null); setSubject(""); setHtml("");
      fetchCampaigns();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="bulk-email-page">
      <div className="flex items-center gap-3 mb-6">
        <Mail className="w-7 h-7 text-burgundy-600" />
        <h1 className="text-2xl font-bold text-stone-900">회원 일괄 이메일</h1>
      </div>

      {/* Segment */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5 mb-5">
        <label className="block text-sm font-semibold text-stone-700 mb-3">수신 대상</label>
        <div className="flex flex-wrap gap-2">
          {SEGMENTS.map((s) => (
            <button key={s.id} onClick={() => setSegment(s.id)} data-testid={`seg-${s.id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${segment === s.id ? "bg-burgundy-600 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2 text-burgundy-700" data-testid="audience-count">
          <Users className="w-4 h-4" />
          <span className="font-bold text-lg">{count === null ? "…" : count.toLocaleString()}</span>
          <span className="text-sm text-stone-500">명 (이메일無·탈퇴·수신거부·테스트·중복 자동 제외)</span>
        </div>
      </div>

      {/* Compose */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5 mb-5">
        <label className="block text-sm font-semibold text-stone-700 mb-2">제목</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject"
          className="w-full rounded-xl border border-stone-200 focus:border-burgundy-400 focus:ring-2 focus:ring-burgundy-100 outline-none px-4 py-2.5 text-[15px] mb-4"
          placeholder="이메일 제목" />
        <label className="block text-sm font-semibold text-stone-700 mb-2">본문 (HTML 가능 · <code>{"{{name}}"}</code> = 회원 이름 개인화)</label>
        <textarea value={html} onChange={(e) => setHtml(e.target.value)} data-testid="email-body"
          className="w-full min-h-[200px] rounded-xl border border-stone-200 focus:border-burgundy-400 focus:ring-2 focus:ring-burgundy-100 outline-none p-4 text-[15px] leading-[1.7] resize-y font-mono"
          placeholder="<p>{{name}}님, 안녕하세요!</p>" />
        <p className="text-xs text-stone-400 mt-2">발신자: New Coupling / LizTV · 수신거부 링크 자동 포함 · 수신자 개별 발송(상호 비노출)</p>
        <button onClick={createCampaign} disabled={busy} data-testid="create-campaign-btn"
          className="mt-4 px-5 py-2.5 rounded-full bg-stone-800 text-white text-sm font-semibold hover:bg-stone-900 disabled:opacity-50 flex items-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} 캠페인 준비
        </button>
      </div>

      {/* Campaign actions */}
      {campaign && (
        <div className="bg-burgundy-50 rounded-2xl border border-burgundy-100 p-5 mb-5" data-testid="campaign-actions">
          <div className="text-sm text-stone-700 mb-3">
            <b>{campaign.subject}</b> · 대상 <b>{campaign.recipient_count?.toLocaleString()}</b>명
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={sendTest} disabled={busy} data-testid="send-test-btn"
              className="px-4 py-2 rounded-full bg-white border border-burgundy-200 text-burgundy-700 text-sm font-semibold hover:bg-burgundy-100 disabled:opacity-50 flex items-center gap-2">
              <Send className="w-4 h-4" /> 테스트 발송(내 이메일)
            </button>
            {campaign.test_sent && (
              <span className="flex items-center gap-1 text-green-600 text-sm" data-testid="test-sent-ok">
                <CheckCircle2 className="w-4 h-4" /> 테스트 완료
              </span>
            )}
            <button onClick={() => setConfirming(true)} disabled={busy || !campaign.test_sent} data-testid="open-confirm-btn"
              className="px-5 py-2 rounded-full bg-burgundy-600 text-white text-sm font-semibold hover:bg-burgundy-700 disabled:opacity-40 flex items-center gap-2">
              <Send className="w-4 h-4" /> 전체 발송
            </button>
          </div>
          {!campaign.test_sent && (
            <p className="text-xs text-stone-500 mt-2 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> 테스트 발송 후에만 전체 발송이 가능합니다.
            </p>
          )}
        </div>
      )}

      {/* Confirm modal */}
      {confirming && campaign && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="confirm-modal">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-stone-900 mb-2">전체 발송 확인</h3>
            <p className="text-sm text-stone-600 leading-relaxed mb-4">
              캠페인 <b>"{campaign.subject}"</b> 을(를)<br />
              <b className="text-burgundy-700">{campaign.recipient_count?.toLocaleString()}명</b>에게 발송합니다.<br />
              발송 후에는 취소할 수 없습니다.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirming(false)} className="px-4 py-2 rounded-full text-sm text-stone-500 hover:bg-stone-100" data-testid="confirm-cancel">취소</button>
              <button onClick={sendReal} disabled={busy} data-testid="confirm-send"
                className="px-5 py-2 rounded-full bg-burgundy-600 text-white text-sm font-semibold hover:bg-burgundy-700 disabled:opacity-50 flex items-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} 발송
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign log */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5" data-testid="campaign-log">
        <h2 className="text-sm font-semibold text-stone-700 mb-3">발송 로그</h2>
        {campaigns.length === 0 ? (
          <p className="text-sm text-stone-400">아직 캠페인이 없습니다.</p>
        ) : (
          <div className="divide-y divide-stone-100">
            {campaigns.map((c) => (
              <div key={c.id} className="py-3 flex items-center justify-between text-sm" data-testid={`campaign-row-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-stone-800 truncate">{c.subject}</div>
                  <div className="text-xs text-stone-400">{(c.created_at || "").slice(0, 10)} · {c.segment} · 대상 {c.recipient_count?.toLocaleString()}</div>
                </div>
                <div className="text-right text-xs shrink-0 ml-3">
                  <span className={`px-2 py-0.5 rounded-full font-medium ${c.status === "sent" ? "bg-green-100 text-green-700" : c.status === "sending" ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-500"}`}>{c.status}</span>
                  <div className="text-stone-500 mt-1">성공 {c.sent || 0} · 실패 {c.failed || 0}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
